/**
 * CircleDetail — Single Circle detail page.
 * Route: /circles/:circleSlug
 *
 * Shows circle info, members preview, and provides Chat/Events/Join actions.
 * Auth model:
 *  - Auth required (Cloud Function requires auth for detail fetch)
 *  - Members can access Chat and Events
 *  - Non-members can join / request to join
 *  - Admins see edit controls
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  XCircle, ArrowLeft, Edit2, Lock, Globe, Users, MapPin,
  MessageCircle, Calendar, Clock, ChevronLeft, UserPlus,
} from 'react-feather';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAuthGate } from '../hooks/useAuthGate';
import { useCircleId } from '../hooks/useCircleId';
import SEO from '../components/SEO';
import GlassPanel from '../components/GlassPanel';
import MonoAvatar from '../components/MonoAvatar';
import CircleInviteSheet from '../components/CircleInviteSheet';
import { getCircleDetail, joinCircle, leaveCircle } from '../services/circleService';
import { slugify } from '../utils/circleSlug';
// PPL-508 — the circles gate: one field, one way out.
import { firstNameGateMessage, FIRST_NAME_ACTION_LABEL, PROFILE_EDIT_PATH } from '../utils/circleGate';
import type { Circle, CircleMember } from '../types/circles';
import '../styles/modern.css';

export default function CircleDetail() {
  const { circleSlug } = useParams<{ circleSlug: string }>();
  const { circleId, resolving } = useCircleId(circleSlug);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAuthenticated, redirectToLogin } = useAuthGate();

  const [circle, setCircle] = useState<Circle | null>(null);
  const [membership, setMembership] = useState<CircleMember | null>(null);
  const [members, setMembers] = useState<CircleMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  // `gate` marks the one refusal the user can act on here — no first name, the
  // only thing a circle needs from a member (PPL-508) — so the banner can carry
  // the way out. The text is always the server's sentence.
  const [message, setMessage] = useState<
    { type: 'success' | 'error' | 'info'; text: string; gate?: boolean } | null
  >(null);
  // PPL-215 — the admin-only "invite people" sheet.
  const [inviteOpen, setInviteOpen] = useState(false);

  // ─── Auth gate ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) {
      redirectToLogin();
    }
  }, [isAuthenticated, redirectToLogin]);

  // ─── Fetch detail ─────────────────────────────────────────────────────
  const loadDetail = useCallback(async () => {
    if (!circleId || !isAuthenticated) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getCircleDetail(circleId);
      setCircle(data.circle);
      setMembership(data.membership);
      setMembers(data.members || []);
    } catch (err: any) {
      setError(err?.message || 'Circle not found');
    } finally {
      setLoading(false);
    }
  }, [circleId, isAuthenticated]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  // ─── Actions ──────────────────────────────────────────────────────────
  const handleJoin = async () => {
    if (!circleId) return;
    setActionLoading(true);
    setMessage(null);

    try {
      const result = await joinCircle(circleId);
      if (result.autoApproved) {
        setMessage({ type: 'success', text: 'You have joined the Circle!' });
      } else {
        setMessage({ type: 'info', text: 'Your join request has been sent to the admins.' });
      }
      await loadDetail();
    } catch (err: any) {
      setMessage({
        type: 'error',
        text: err?.message || 'Failed to join circle',
        gate: firstNameGateMessage(err) !== null,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleLeave = async () => {
    if (!circleId || !confirm('Are you sure you want to leave this Circle?')) return;
    setActionLoading(true);
    setMessage(null);

    try {
      await leaveCircle(circleId);
      setMessage({ type: 'info', text: 'You have left the Circle.' });
      await loadDetail();
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.message || 'Failed to leave circle' });
    } finally {
      setActionLoading(false);
    }
  };

  // ─── Derived state ────────────────────────────────────────────────────
  const isMember = membership?.status === 'active';
  const isPending = membership?.status === 'pending';
  const isAdmin = membership?.role === 'admin';
  // Inviting needs an ACTIVE admin, matching what the server's assertActiveAdmin
  // enforces and what CircleMembers gates on — an admin whose own membership is
  // pending/kicked/banned would otherwise get an invite affordance that can only
  // fail server-side. Deliberately separate from the pre-existing `isAdmin`,
  // which gates unrelated controls and is left as-is.
  const canInvite = isAdmin && membership?.status === 'active';
  const isFull = circle ? circle.memberCount >= circle.memberLimit : false;

  // ─── Render ─────────────────────────────────────────────────────────────

  if (!isAuthenticated) return null;

  return (
    <div className="page-shell">
      <div className="page-shell__inner" style={{ maxWidth: 780 }}>
        {circle && (
          <SEO
            title={`${circle.name} — Circle | pplkpr`}
            description={circle.description.slice(0, 160)}
            path={`/circles/${circleSlug}`}
          />
        )}

        {/* Loading */}
        {(loading || resolving) && (
          <div style={{ padding: '3rem 0' }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <GlassPanel key={i} hover={false} style={{ marginBottom: 12 }}>
                <div className="skel-line wide pulse" style={{ height: 18, marginBottom: 10 }} />
                <div className="skel-line medium pulse" style={{ height: 12, marginBottom: 4 }} />
                <div className="skel-line narrow pulse" style={{ height: 12 }} />
              </GlassPanel>
            ))}
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
            <XCircle size={32} style={{ color: 'rgba(220,200,240,0.5)', marginBottom: 12 }} />
            <h2 style={{ color: '#fff', fontSize: 20, marginBottom: 8 }}>circle not found</h2>
            <p style={{ color: 'rgba(220,200,240,0.7)', marginBottom: 20 }}>{error}</p>
            <button className="primary-button" onClick={() => navigate('/circles')}>
              browse circles
            </button>
          </div>
        )}

        {circle && !loading && (
          <>
            {/* Back nav + admin edit */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <button
                onClick={() => navigate('/circles')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 999, padding: '7px 14px', color: '#fff', fontSize: 13,
                  fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                <ChevronLeft size={14} /> circles
              </button>
              {isAdmin && (
                <button
                  onClick={() => navigate(`/circles/${circleSlug}/edit`, { state: { circleId } })}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 999, padding: '7px 14px', color: '#fff', fontSize: 13,
                    fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  <Edit2 size={14} /> edit
                </button>
              )}
            </div>

            {/* Message banner */}
            {message && (
              <div className={`circle-message-banner ${message.type}`} style={{ marginBottom: 16 }}>
                {message.text}
                {message.gate && (
                  <>
                    {' '}
                    <Link
                      to={PROFILE_EDIT_PATH}
                      style={{ color: 'inherit', fontWeight: 600, textDecoration: 'underline' }}
                    >
                      {FIRST_NAME_ACTION_LABEL}
                    </Link>
                  </>
                )}
              </div>
            )}

            {/* Circle info card */}
            <GlassPanel hover={false} padding={0} style={{ marginBottom: 16 }}>
              {/* Gradient stripe */}
              <div style={{
                height: 6,
                background: 'linear-gradient(90deg, #d946ef, #8b5cf6)',
                borderRadius: '16px 16px 0 0',
              }} />
              <div style={{ padding: 24 }}>
                <h1 style={{
                  fontSize: 26, fontWeight: 800, color: '#fff', margin: '0 0 12px',
                  display: 'flex', alignItems: 'center', gap: 8, letterSpacing: '-0.025em',
                }}>
                  {circle.visibility === 'private' && (
                    <Lock size={20} style={{ opacity: 0.5 }} />
                  )}
                  {circle.name}
                </h1>

                <div style={{
                  display: 'flex', flexWrap: 'wrap', gap: 14,
                  fontSize: 13, color: 'rgba(220,200,240,0.7)', marginBottom: 14,
                }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <Globe size={14} /> {circle.visibility === 'public' ? 'public' : 'private'}
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <Users size={14} /> {circle.memberCount}/{circle.memberLimit} members
                  </span>
                  {circle.location && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <MapPin size={14} /> {circle.location}
                    </span>
                  )}
                </div>

                <p style={{
                  fontSize: 14, lineHeight: 1.6, color: 'rgba(220,200,240,0.85)',
                  margin: 0,
                }}>
                  {circle.description}
                </p>

                {circle.categoryTags && circle.categoryTags.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 14 }}>
                    {circle.categoryTags.map((tag) => (
                      <span key={tag} className="tag-pill tag-pill--sm tag-pill--violet">{tag}</span>
                    ))}
                  </div>
                )}
              </div>
            </GlassPanel>

            {/* Members preview */}
            <GlassPanel hover={false} style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#fff' }}>
                  members ({circle.memberCount})
                </h3>
                {isMember && (
                  <Link
                    to={`/circles/${circleSlug}/members`}
                    state={{ circleId }}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      fontSize: 12, color: 'rgba(220,200,240,0.7)', textDecoration: 'none',
                    }}
                  >
                    <Users size={12} /> view all
                  </Link>
                )}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {members.slice(0, 8).map((m) => (
                  <div
                    key={m.userId}
                    style={{ cursor: 'pointer', position: 'relative' }}
                    title={m.tetherHandle || m.firstName || 'Member'}
                    onClick={() => navigate(`/user/${m.userId}`)}
                  >
                    <MonoAvatar
                      name={[m.firstName, m.lastName].filter(Boolean).join(' ') || m.tetherHandle || m.userId?.slice(0, 4) || 'M'}
                      size={40}
                    />
                    {m.role === 'admin' && (
                      <span style={{
                        position: 'absolute', bottom: 0, right: 0,
                        width: 10, height: 10, borderRadius: '50%',
                        background: '#a78bfa', border: '2px solid #1a0e22',
                      }} />
                    )}
                  </div>
                ))}
                {circle.memberCount > 8 && (
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%',
                    background: 'rgba(255,255,255,0.06)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, color: 'rgba(220,200,240,0.5)', fontWeight: 600,
                  }}>
                    +{circle.memberCount - 8}
                  </div>
                )}
              </div>
            </GlassPanel>

            {/* Action buttons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
              {isMember && (
                <>
                  <Link
                    to={`/circles/${circleSlug}/chat`}
                    state={{ circleId }}
                    className="primary-button"
                    style={{ textDecoration: 'none' }}
                  >
                    <MessageCircle size={14} /> chat
                  </Link>
                  <button
                    onClick={() => navigate(`/circles/${circleSlug}/events`, { state: { circleId } })}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 12, padding: '10px 18px', color: '#fff', fontSize: 13.5,
                      fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    <Calendar size={14} /> events
                  </button>
                  {/* PPL-215 — inviting is admin-only in v1. The gate is
                      enforced server-side (assertActiveAdmin runs before any
                      invitee lookup); this is just the affordance. */}
                  {canInvite && (
                    <button
                      onClick={() => setInviteOpen(true)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        background: 'rgba(217,70,239,0.12)', border: '1px solid rgba(217,70,239,0.35)',
                        borderRadius: 12, padding: '10px 18px', color: '#f0abfc', fontSize: 13.5,
                        fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                      }}
                    >
                      <UserPlus size={14} /> invite people
                    </button>
                  )}
                  <button
                    onClick={handleLeave}
                    disabled={actionLoading}
                    style={{
                      marginLeft: 'auto', background: 'none',
                      border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
                      padding: '7px 14px', color: 'rgba(220,200,240,0.6)', fontSize: 13,
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    leave circle
                  </button>
                </>
              )}

              {!isMember && !isPending && (
                <button
                  className="primary-button"
                  onClick={handleJoin}
                  disabled={actionLoading || isFull}
                >
                  {isFull
                    ? 'circle full'
                    : actionLoading
                      ? 'joining...'
                      : 'join circle'}
                </button>
              )}

              {isPending && (
                <button
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 12, padding: '10px 18px', color: 'rgba(220,200,240,0.6)',
                    fontSize: 13.5, fontWeight: 600, fontFamily: 'inherit', cursor: 'default',
                  }}
                  disabled
                >
                  <Clock size={14} /> request pending
                </button>
              )}
            </div>

            {/* Status banners */}
            {circle.status === 'archived' && (
              <div className="circle-message-banner info">
                This Circle has been archived and is no longer active.
              </div>
            )}

          </>
        )}

        {/* PPL-215 — invite sheet (admins only). Rendered OUTSIDE the
            `!loading` gate above: refreshing the detail flips `loading` back
            on, which would unmount the sheet mid-flow and throw away its
            success state. The member count is refreshed on close instead. */}
        {inviteOpen && canInvite && circleId && circle && (
          <CircleInviteSheet
            circleId={circleId}
            circleName={circle.name}
            uid={user?.uid ?? null}
            onClose={() => { setInviteOpen(false); void loadDetail(); }}
          />
        )}
      </div>
    </div>
  );
}
